import { BALANCE } from '../balance';
import type { UnlockDefInput } from '../schemas/progressionDefs';

/**
 * The 15 MVP unlocks plus Bếp của bà (content-schemas §16.1, economy-progression §5, minigames.md §4.2) and
 * Studio NANO with the Tiếng lành levels that open something (studio-nano.md §2, §5). Star thresholds and paid costs come from
 * BALANCE; items, products, mini games and wall/floor patterns point at these IDs through their own `unlockId`.
 */
const T = BALANCE.starThresholds;
const stars = (value: number) => ({ type: 'starsAtLeast', value }) as const;
const after = <U extends `unl_${string}`>(unlockId: U) => ({ type: 'unlocked', unlockId }) as const;
const coins = (amount: number) => ({ type: 'coinsCost', amount }) as const;
const feature = <F extends string>(f: F) => ({ type: 'feature', feature: f }) as const;
const metFlag = <N extends string>(npc: N) =>
  ({ type: 'flag', flag: `flag_npc_met_${npc}`, value: true }) as const;
const sticker = <S extends `stk_${string}`>(stickerId: S) =>
  ({ type: 'reward', reward: { type: 'sticker', stickerId } }) as const;
const studioLevel = (level: number) => ({ type: 'studioLevelAtLeast', level }) as const;

export const UNLOCKS = [
  {
    id: 'unl_home',
    nameKey: 'unl.unl_home.name',
    reasonKey: 'unl.unl_home.reason',
    prerequisites: [],
    grants: [{ type: 'scene', sceneId: 'home' }, feature('catalog')],
    order: 1,
  },
  {
    id: 'unl_dusting',
    nameKey: 'unl.unl_dusting.name',
    reasonKey: 'unl.unl_dusting.reason',
    prerequisites: [{ type: 'taskCompleted', taskId: 'T05' }],
    grants: [],
    order: 2,
  },
  {
    id: 'unl_minimart',
    nameKey: 'unl.unl_minimart.name',
    reasonKey: 'unl.unl_minimart.reason',
    prerequisites: [{ type: 'taskCompleted', taskId: 'T10' }],
    grants: [
      { type: 'scene', sceneId: 'minimart' },
      { type: 'scene', sceneId: 'townmap' },
      feature('shop'),
      feature('townmap'),
      feature('mailbox'),
      { type: 'chapter', chapterId: 'ch2' },
      metFlag('npc_lan'),
      metFlag('npc_bao'),
      metFlag('npc_mit'),
      metFlag('npc_boss_cat'),
    ],
    order: 3,
  },
  {
    id: 'unl_npc_orders',
    nameKey: 'unl.unl_npc_orders.name',
    reasonKey: 'unl.unl_npc_orders.reason',
    prerequisites: [stars(T.npcOrders), after('unl_minimart')],
    grants: [feature('npcOrders')],
    order: 4,
  },
  {
    id: 'unl_packing',
    nameKey: 'unl.unl_packing.name',
    reasonKey: 'unl.unl_packing.reason',
    prerequisites: [stars(T.packing), after('unl_minimart')],
    grants: [],
    order: 5,
  },
  {
    id: 'unl_products_t2',
    nameKey: 'unl.unl_products_t2.name',
    reasonKey: 'unl.unl_products_t2.reason',
    prerequisites: [stars(T.productsT2), after('unl_minimart')],
    grants: [],
    order: 6,
  },
  {
    id: 'unl_catalog_t2',
    nameKey: 'unl.unl_catalog_t2.name',
    reasonKey: 'unl.unl_catalog_t2.reason',
    prerequisites: [stars(T.catalogT2)],
    grants: [],
    order: 7,
  },
  {
    id: 'unl_photo_mode',
    nameKey: 'unl.unl_photo_mode.name',
    reasonKey: 'unl.unl_photo_mode.reason',
    // economy §5: 30 stars + chapter 2 seen (which implies the Tiệm is open; kept explicit for the DAG).
    prerequisites: [stars(T.photoMode), after('unl_minimart'), { type: 'chapterSeen', chapterId: 'ch2' }],
    grants: [
      { type: 'chapter', chapterId: 'ch3' },
      feature('photoMode'),
      feature('albumPhotos'),
      { type: 'reward', reward: { type: 'item', itemId: 'dec_grandma_camera', qty: 1 } },
      { type: 'reward', reward: { type: 'photoFrame', frameId: 'frm_polaroid' } },
      sticker('stk_default_1'),
      sticker('stk_default_2'),
      sticker('stk_default_3'),
      sticker('stk_default_4'),
      // Lets dialogue nodes branch on "Photo mode not open yet" (Bé Mít lvup1 .3 vs .4); conditions have no NOT.
      { type: 'flag', flag: 'flag_photo_mode', value: true },
    ],
    order: 8,
  },
  {
    id: 'unl_palette_2',
    nameKey: 'unl.unl_palette_2.name',
    reasonKey: 'unl.unl_palette_2.reason',
    prerequisites: [stars(T.palette2)],
    grants: [],
    order: 9,
  },
  {
    id: 'unl_shop_expansion_1',
    nameKey: 'unl.unl_shop_expansion_1.name',
    reasonKey: 'unl.unl_shop_expansion_1.reason',
    prerequisites: [
      stars(T.shopExpansion1),
      after('unl_minimart'),
      coins(BALANCE.unlockCosts.shopExpansion1),
    ],
    grants: [{ type: 'roomExpansion', roomId: 'minimart', expansionId: 'exp_minimart_1' }],
    order: 10,
  },
  {
    id: 'unl_products_t3',
    nameKey: 'unl.unl_products_t3.name',
    reasonKey: 'unl.unl_products_t3.reason',
    prerequisites: [stars(T.productsT3)],
    grants: [],
    order: 11,
  },
  {
    id: 'unl_catalog_t3',
    nameKey: 'unl.unl_catalog_t3.name',
    reasonKey: 'unl.unl_catalog_t3.reason',
    prerequisites: [stars(T.catalogT3)],
    grants: [],
    order: 12,
  },
  {
    id: 'unl_chapter_4',
    nameKey: 'unl.unl_chapter_4.name',
    reasonKey: 'unl.unl_chapter_4.reason',
    prerequisites: [stars(T.chapter4), after('unl_photo_mode')],
    grants: [
      { type: 'chapter', chapterId: 'ch4' },
      { type: 'reward', reward: { type: 'item', itemId: 'dec_grandma_photo', qty: 1 } },
    ],
    order: 13,
  },
  {
    id: 'unl_home_layout_2',
    nameKey: 'unl.unl_home_layout_2.name',
    reasonKey: 'unl.unl_home_layout_2.reason',
    prerequisites: [stars(T.homeLayout2), coins(BALANCE.unlockCosts.homeLayout2)],
    grants: [{ type: 'roomExpansion', roomId: 'home', expansionId: 'exp_home_sleep_nook' }],
    order: 14,
  },
  {
    id: 'unl_chapter_5',
    nameKey: 'unl.unl_chapter_5.name',
    reasonKey: 'unl.unl_chapter_5.reason',
    prerequisites: [stars(T.chapter5), after('unl_chapter_4')],
    // The special postcard goes to mailbox.special (content-schemas §6.1); flag_studio_key is its attachment.
    grants: [
      { type: 'chapter', chapterId: 'ch5' },
      { type: 'reward', reward: { type: 'postcard', postcardId: 'pc_ch5_key', isRepeat: false } },
    ],
    // Tease stays hidden until granted (content-schemas §4.1).
    previewWhenLocked: false,
    order: 15,
  },
  {
    // Bếp của bà (minigames.md §4.2): Granny's jam recipe found (dusting level 3) and the kitchen unpacked.
    // No star gate: finding the recipe is the key, not being rich enough.
    id: 'unl_cooking',
    nameKey: 'unl.unl_cooking.name',
    reasonKey: 'unl.unl_cooking.reason',
    prerequisites: [
      { type: 'miniGameCleared', miniGameId: 'mg_dusting', level: 3 },
      { type: 'boxOpened', boxDefId: 'box_04_kitchen' },
      after('unl_minimart'),
    ],
    grants: [],
    order: 16,
  },
  {
    // Studio NANO (studio-nano.md §2): Granny's second key, chapter 5. The player pays to fix it up.
    id: 'unl_studio',
    nameKey: 'unl.unl_studio.name',
    reasonKey: 'unl.unl_studio.reason',
    prerequisites: [
      after('unl_chapter_5'),
      { type: 'chapterSeen', chapterId: 'ch5' },
      coins(BALANCE.unlockCosts.studio),
    ],
    grants: [
      { type: 'scene', sceneId: 'studio' },
      feature('studio'),
      { type: 'chapter', chapterId: 'ch6' },
      { type: 'reward', reward: { type: 'item', itemId: 'fur_studio_backdrop', qty: 1 } },
      { type: 'reward', reward: { type: 'item', itemId: 'dec_studio_softbox', qty: 1 } },
      { type: 'reward', reward: { type: 'item', itemId: 'dec_studio_tripod', qty: 1 } },
    ],
    // Like chapter 5: no hint of the studio before Granny tells about it.
    previewWhenLocked: false,
    order: 17,
  },
  // Tiếng lành levels that open something (studio-nano.md §5); stars and coins per level are in data/studio.ts.
  {
    id: 'unl_studio_lv2',
    nameKey: 'unl.unl_studio_lv2.name',
    reasonKey: 'unl.unl_studio_lv2.reason',
    // Gates the second set of studio props in the catalog and the bookings that need them.
    prerequisites: [studioLevel(2)],
    grants: [],
    previewWhenLocked: false,
    order: 18,
  },
  {
    id: 'unl_studio_lv3',
    nameKey: 'unl.unl_studio_lv3.name',
    reasonKey: 'unl.unl_studio_lv3.reason',
    prerequisites: [studioLevel(3)],
    grants: [
      { type: 'roomExpansion', roomId: 'studio', expansionId: 'exp_studio_1' },
      { type: 'chapter', chapterId: 'ch7' },
    ],
    previewWhenLocked: false,
    order: 19,
  },
  {
    id: 'unl_studio_lv5',
    nameKey: 'unl.unl_studio_lv5.name',
    reasonKey: 'unl.unl_studio_lv5.reason',
    prerequisites: [studioLevel(5)],
    grants: [{ type: 'chapter', chapterId: 'ch8' }],
    previewWhenLocked: false,
    order: 20,
  },
] as const satisfies readonly UnlockDefInput[];
